/**
 * Skeleton Loader Utility
 * Facebook-style loading placeholders
 */

class SkeletonLoader {
  constructor() {
    this.observers = new Map();
    this.init();
  }

  init() {
    // Auto-detect skeleton wrappers and manage loading states
    document.addEventListener("DOMContentLoaded", () => {
      this.initializeSkeletons();
    });
  }

  /**
   * Initialize all skeleton loaders on the page
   */
  initializeSkeletons() {
    const wrappers = document.querySelectorAll(".skeleton-wrapper");

    wrappers.forEach((wrapper) => {
      // Start in loading state if data attribute present
      if (wrapper.dataset.loading !== "false") {
        this.showSkeleton(wrapper);
      }
    });
  }

  /**
   * Show skeleton loader
   * @param {HTMLElement|string} target - Element or selector
   */
  showSkeleton(target) {
    const element =
      typeof target === "string" ? document.querySelector(target) : target;
    if (!element) return;

    element.classList.add("skeleton-loading");
    element.setAttribute("aria-busy", "true");

    // Hide actual content
    const contentElements = element.querySelectorAll(".skeleton-hide");
    contentElements.forEach((el) => (el.style.display = "none"));
  }

  /**
   * Hide skeleton loader and show content
   * @param {HTMLElement|string} target - Element or selector
   */
  hideSkeleton(target) {
    const element =
      typeof target === "string" ? document.querySelector(target) : target;
    if (!element) return;

    element.classList.remove("skeleton-loading");
    element.removeAttribute("aria-busy");

    // Show actual content
    const contentElements = element.querySelectorAll(".skeleton-hide");
    contentElements.forEach((el) => (el.style.display = ""));

    // Trigger fade-in animation
    element.classList.add("skeleton-loaded");
  }

  /**
   * Create a skeleton placeholder for an image
   * @param {HTMLImageElement} img - Image element
   */
  createImageSkeleton(img) {
    if (img.complete) return;

    const skeleton = document.createElement("div");
    skeleton.className = "skeleton skeleton-image";
    skeleton.style.position = "absolute";
    skeleton.style.top = "0";
    skeleton.style.left = "0";
    skeleton.style.width = "100%";
    skeleton.style.height = "100%";

    const parent = img.parentElement;
    if (parent.style.position === "" || parent.style.position === "static") {
      parent.style.position = "relative";
    }

    parent.insertBefore(skeleton, img);
    img.style.opacity = "0";

    img.addEventListener(
      "load",
      () => {
        skeleton.remove();
        img.style.opacity = "1";
        img.style.transition = "opacity 0.3s ease-in-out";
      },
      { once: true },
    );
  }

  /**
   * Lazy load images with skeleton placeholders
   */
  lazyLoadImages() {
    const images = document.querySelectorAll("img[data-skeleton]");

    images.forEach((img) => {
      this.createImageSkeleton(img);
    });
  }

  /**
   * Create event card skeleton
   * @returns {HTMLElement}
   */
  createEventCardSkeleton() {
    const card = document.createElement("div");
    card.className = "skeleton-event-card";
    card.innerHTML = `
      <div class="skeleton skeleton-image"></div>
      <div class="skeleton-content">
        <div class="skeleton skeleton-title"></div>
        <div class="skeleton skeleton-text skeleton-w-75"></div>
        <div class="skeleton skeleton-text skeleton-w-50"></div>
        <div class="skeleton skeleton-button"></div>
      </div>
    `;
    return card;
  }

  /**
   * Create blog post skeleton
   * @returns {HTMLElement}
   */
  createBlogPostSkeleton() {
    const post = document.createElement("div");
    post.className = "skeleton-blog-post";
    post.innerHTML = `
      <div class="skeleton skeleton-image skeleton-mb-2"></div>
      <div class="skeleton skeleton-title skeleton-mb-2"></div>
      <div class="skeleton skeleton-text skeleton-mb-1"></div>
      <div class="skeleton skeleton-text skeleton-mb-1"></div>
      <div class="skeleton skeleton-text skeleton-w-75"></div>
    `;
    return post;
  }

  /**
   * Create list item skeleton
   * @returns {HTMLElement}
   */
  createListItemSkeleton() {
    const item = document.createElement("div");
    item.className = "skeleton-list-item";
    item.innerHTML = `
      <div class="skeleton skeleton-avatar"></div>
      <div class="skeleton-list-item-body">
        <div class="skeleton skeleton-text skeleton-w-75"></div>
        <div class="skeleton skeleton-text skeleton-text-sm skeleton-w-50"></div>
      </div>
    `;
    return item;
  }

  /**
   * Create comment skeleton
   * @returns {HTMLElement}
   */
  createCommentSkeleton() {
    const comment = document.createElement("div");
    comment.className = "skeleton-comment";
    comment.innerHTML = `
      <div class="skeleton skeleton-avatar skeleton-avatar-sm"></div>
      <div class="skeleton-comment-body">
        <div class="skeleton skeleton-text skeleton-text-sm skeleton-w-25"></div>
        <div class="skeleton skeleton-text skeleton-w-full"></div>
        <div class="skeleton skeleton-text skeleton-w-75"></div>
      </div>
    `;
    return comment;
  }

  /**
   * Populate container with skeleton items
   * @param {string} selector - Container selector
   * @param {string} type - Skeleton type (event, blog, list, comment)
   * @param {number} count - Number of skeletons to create
   */
  populate(selector, type = "list", count = 3) {
    const container = document.querySelector(selector);
    if (!container) return;

    container.innerHTML = "";

    for (let i = 0; i < count; i++) {
      let skeleton;

      switch (type) {
        case "event":
          skeleton = this.createEventCardSkeleton();
          break;
        case "blog":
          skeleton = this.createBlogPostSkeleton();
          break;
        case "comment":
          skeleton = this.createCommentSkeleton();
          break;
        case "list":
        default:
          skeleton = this.createListItemSkeleton();
      }

      container.appendChild(skeleton);
    }
  }
}

// Initialize skeleton loader
const skeletonLoader = new SkeletonLoader();

// Export for use in modules
if (typeof module !== "undefined" && module.exports) {
  module.exports = SkeletonLoader;
}

// Make available globally
window.SkeletonLoader = SkeletonLoader;
window.skeletonLoader = skeletonLoader;

/* ========================================================================
   Section & Widget Skeleton System
   Progressive scroll-reveal + per-widget loading skeletons
   ======================================================================== */

(function () {
  "use strict";

  /* Widget root selectors whose content needs a shimmer overlay */
  var WIDGET_SEL = [
    ".gallery-widget",
    ".testimonials-section",
    ".statistics-section",
    ".blog-post-list-widget",
    ".faq-widget",
    ".price-package-widget",
    ".process-steps-widget",
    ".text-columns-section",
    ".map-location-widget",
    ".contact-info-widget",
    ".features-section",
    ".category-tile",
    '[class*="restaurant-menu"]',
    'section[class*="event"]',
  ].join(",");

  /* Selectors that indicate a dark background */
  var DARK_SEL = ".bg-dark,.bg-dark-gray,.bg-black,[class*='bg-gradient-dark']";

  /* ---- helpers -------------------------------------------------------- */

  function isDark(el) {
    try {
      return el.matches(DARK_SEL) || !!el.closest(DARK_SEL);
    } catch (_) {
      return false;
    }
  }

  /** Return a Promise that resolves when every <img> inside `root` has loaded
      (or after `timeout` ms, whichever comes first). */
  function whenImagesReady(root, timeout) {
    var imgs = root.querySelectorAll("img");
    var promises = [];
    imgs.forEach(function (img) {
      if (!img.complete) {
        promises.push(
          new Promise(function (ok) {
            img.addEventListener("load", ok, { once: true });
            img.addEventListener("error", ok, { once: true });
          }),
        );
      }
    });
    // Also wait for <iframe> (maps, embedded video)
    root.querySelectorAll("iframe").forEach(function (f) {
      promises.push(
        new Promise(function (ok) {
          f.addEventListener("load", ok, { once: true });
          setTimeout(ok, 3000);
        }),
      );
    });
    return Promise.race([
      Promise.all(promises),
      new Promise(function (ok) {
        setTimeout(ok, timeout || 2000);
      }),
    ]);
  }

  /* ---- section scroll-reveal ---------------------------------------- */

  function initSectionReveal() {
    var vh = window.innerHeight;
    var mainEl =
      document.querySelector("main") || document.querySelector("[role='main']");
    if (!mainEl) return;

    // Gather top-level sections (before main.js wraps them in .page-layout)
    var sections = mainEl.querySelectorAll("section");
    var belowFold = [];

    sections.forEach(function (sec) {
      var top = sec.getBoundingClientRect().top;
      if (top >= vh) {
        sec.classList.add("sk-hidden");
        belowFold.push(sec);
      }
    });

    if (!belowFold.length) return;

    if ("IntersectionObserver" in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              revealSection(entry.target);
              observer.unobserve(entry.target);
            }
          });
        },
        { rootMargin: "80px 0px", threshold: 0.05 },
      );

      belowFold.forEach(function (sec) {
        observer.observe(sec);
      });
    } else {
      // No IntersectionObserver — reveal everything immediately
      belowFold.forEach(function (sec) {
        sec.classList.remove("sk-hidden");
      });
    }
  }

  function revealSection(section) {
    whenImagesReady(section, 2000).then(function () {
      section.classList.add("sk-revealed");
      section.classList.remove("sk-hidden");
    });
  }

  /* ---- widget shimmer overlays -------------------------------------- */

  function initWidgetSkeletons() {
    var widgets = document.querySelectorAll(WIDGET_SEL);
    if (!widgets.length) return;

    widgets.forEach(function (w) {
      var imgs = w.querySelectorAll("img");
      var iframes = w.querySelectorAll("iframe");
      var hasMedia = imgs.length > 0 || iframes.length > 0;
      if (!hasMedia) return;

      // Skip if everything is already loaded
      var allDone = Array.from(imgs).every(function (i) {
        return i.complete;
      });
      if (allDone && !iframes.length) return;

      w.classList.add("sk-widget-loading");
      if (isDark(w)) w.classList.add("sk-dark");

      whenImagesReady(w, 2500).then(function () {
        w.classList.add("sk-widget-loaded");
        // Remove classes after CSS transition ends
        setTimeout(function () {
          w.classList.remove(
            "sk-widget-loading",
            "sk-widget-loaded",
            "sk-dark",
          );
        }, 500);
      });
    });
  }

  /* ---- image-level shimmer ------------------------------------------ */

  function initImageSkeletons() {
    var lazyImgs = document.querySelectorAll('img[loading="lazy"]');

    lazyImgs.forEach(function (img) {
      if (img.complete) return;

      img.classList.add("sk-img-loading");
      var sec = img.closest("section");
      if (sec && isDark(sec)) img.setAttribute("data-sk-dark", "");

      function done() {
        img.classList.remove("sk-img-loading");
        img.removeAttribute("data-sk-dark");
      }
      img.addEventListener("load", done, { once: true });
      img.addEventListener("error", done, { once: true });
    });
  }

  /* ---- boot --------------------------------------------------------- */

  function boot() {
    initSectionReveal();
    initWidgetSkeletons();
    initImageSkeletons();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
